import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_AURORA } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/hope.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/hope.mp3',
  attribution: '"Hope" by Morley & Speo [NCS Release] - https://ncs.io/Hope',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('hope', variant),
    slug: buildVariantSlug('hope', variant),
    title: buildVariantTitle('Hope', variant),
    description:
      'Easiest after On & On. 140 BPM melodic dubstep with a vocal-led build. Sparse pads through the verse, light groove through the drop.',
    bpm: onsets.bpm,
    difficulty: 1,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_AURORA,
    audio,
  });

  // 60s @ 140 BPM ≈ 140 beats. Verse + build are vocal-led (no kicks until
  // the drop at ~beat 57); chart accordingly with leads first then percussive
  // pads after the drop.
  builder.section({ startBeat: 0, endBeat: 32, kind: 'verse' });
  builder.section({ startBeat: 32, endBeat: 57, kind: 'bridge' });
  builder.section({ startBeat: 57, endBeat: 128, kind: 'drop' });
  builder.section({ startBeat: 128, endBeat: 140, kind: 'chorus' });

  // Verse: prominent vocal/synth leads. The detector classifies any onset
  // at strength <=0.25 as 'lead', so leads are bounded at intensity 0.25 by
  // construction; 0.18+ catches just the phrase peaks for a spacious verse.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 32,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 3,
  });

  // Build: leads + early snares. Spacing 2 keeps the lead-up unhurried.
  const buildPads = variantSelectBeats(variant, onsets, {
    fromBeat: 32,
    untilBeat: 57,
    kinds: ['lead', 'snare'],
    minIntensity: 0.2,
    minSpacing: 2,
  });

  // Drop: kicks + strong snares. 1.5-beat spacing at 140 BPM ≈ 643ms - still
  // firmly in easy-tempo territory.
  const dropPads = variantSelectBeats(variant, onsets, {
    fromBeat: 57,
    untilBeat: 128,
    kinds: ['kick', 'snare'],
    minIntensity: 0.35,
    minSpacing: 1.5,
  });

  // Outro: tail of the vocal.
  const outroPads = variantSelectBeats(variant, onsets, {
    fromBeat: 128,
    untilBeat: 138,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 2,
  });

  const padBeats = mergeBeats(versePads, buildPads, dropPads, outroPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // Long lead-only stretches in the verse and a short break before the drop
  // both invite mash chambers; minGap 5 keeps even short quiet moments active.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 138,
    minGapBeats: 5,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(140);
  return builder.build();
}

export const HOPE_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const HOPE_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(HOPE_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
